import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { auditLogsApi } from '../../lib/api'
import DataTable, { Column } from '../../components/shared/DataTable'
import FilterBar from '../../components/shared/FilterBar'
import DetailDrawer from '../../components/shared/DetailDrawer'
import JsonViewer from '../../components/shared/JsonViewer'
import Pagination from '../../components/shared/Pagination'
import ErrorState from '../../components/shared/ErrorState'
import { formatDatetime } from '../../lib/utils'
import type { AuditLog } from '../../types'
import { tx, lang } from '../../lib/i18n'

// Values ending in "." match every action of that group (server: startsWith).
const actionOptions = [
  { label: tx('Shop Suspended'), value: 'shop.suspended' },
  { label: tx('Shop Reactivated'), value: 'shop.reactivated' },
  { label: tx('Plan Changed'), value: 'subscription.plan_changed' },
  { label: tx('Invoice Paid'), value: 'invoice.paid' },
  { label: tx('Flag Updated'), value: 'feature_flag.updated' },
  { label: tx('Admin Created'), value: 'admin.user_created' },
  { label: tx('Roles & permissions (all)'), value: 'admin_role.' },
  { label: tx('Personal permissions changed'), value: 'admin.permissions_updated' },
  { label: tx('Permission denied'), value: 'permission.denied' },
  { label: tx('Sign-in'), value: 'admin.login' },
  { label: tx('Failed sign-in'), value: 'admin.login_failed' },
  { label: tx('Admin accounts (all)'), value: 'admin.' },
  { label: tx('Shops (all)'), value: 'shop.' },
  { label: tx('Shop subscriptions (all)'), value: 'subscription.' },
  { label: tx('Billing & money (all)'), value: 'billing.' },
  { label: tx('Plans (all)'), value: 'plan.' },
  { label: tx('Plan promotions (all)'), value: 'subscription_promotion.' },
  { label: tx('Feature flags (all)'), value: 'feature_flag.' },
  { label: tx('Menu control (all)'), value: 'menu_config.' },
  { label: tx('Business types (all)'), value: 'business_type.' },
  { label: tx('Payment methods (all)'), value: 'payment_method.' },
  { label: tx('Notifications (all)'), value: 'notification' },
  { label: tx('Support tickets (all)'), value: 'support.' },
  { label: tx('System settings (all)'), value: 'system.' },
]

/** Thai label for an action code (the code itself is always shown too). */
const ACTION_TH: Record<string, string> = {
  'admin.login': 'เข้าสู่ระบบ',
  'admin.login_failed': 'เข้าสู่ระบบไม่สำเร็จ',
  'admin.logout': 'ออกจากระบบ',
  'admin.user_created': 'สร้างผู้ดูแล',
  'admin.user_updated': 'แก้ไขผู้ดูแล',
  'admin.user_deactivated': 'ปิดใช้งานผู้ดูแล',
  'admin.user_activated': 'เปิดใช้งานผู้ดูแล',
  'admin.role_changed': 'เปลี่ยนบทบาทผู้ดูแล',
  'admin.permissions_updated': 'ปรับสิทธิ์รายคน',
  'admin.password_reset': 'ตั้งรหัสผ่านใหม่ให้',
  'admin.password_changed': 'เปลี่ยนรหัสผ่านตัวเอง',
  'admin.2fa_reset': 'รีเซ็ต 2FA',
  'admin.2fa_enabled': 'เปิด 2FA',
  'admin.2fa_disabled': 'ปิด 2FA',
  'admin_role.created': 'สร้างบทบาท',
  'admin_role.updated': 'แก้สิทธิ์บทบาท',
  'admin_role.deleted': 'ลบบทบาท',
  'permission.denied': 'ถูกปฏิเสธ (ไม่มีสิทธิ์)',
  'shop.suspended': 'ระงับร้าน',
  'shop.reactivated': 'เปิดร้านอีกครั้ง',
  'shop.info_updated': 'แก้ตั้งค่าผู้ดูแลของร้าน',
  'shop.exported': 'ส่งออกรายชื่อร้าน (CSV)',
  'subscription.plan_changed': 'เปลี่ยนแพ็กเกจ',
  'subscription.extended': 'ขยายเวลาแพ็กเกจ',
  'subscription.cancelled': 'ยกเลิกแพ็กเกจ',
  'subscription.reactivated': 'เปิดแพ็กเกจอีกครั้ง',
  'subscription.note_updated': 'แก้บันทึกแพ็กเกจ',
  'billing.manual_payment': 'บันทึกการชำระเอง',
  'billing.credit_adjustment': 'ปรับยอด (เพิ่ม)',
  'billing.debit_adjustment': 'ปรับยอด (หัก)',
  'plan.created': 'สร้างแพ็กเกจ',
  'plan.updated': 'แก้แพ็กเกจ',
  'plan.price_scheduled': 'ตั้งราคาแพ็กเกจใหม่',
  'feature_flag.created': 'สร้างฟีเจอร์',
  'feature_flag.updated': 'แก้ฟีเจอร์',
  'feature_flag.override_added': 'เพิ่มการกำหนดเฉพาะร้าน',
  'feature_flag.override_removed': 'ลบการกำหนดเฉพาะร้าน',
  'menu_config.created': 'สร้างการตั้งค่าเมนู',
  'menu_config.updated': 'แก้การตั้งค่าเมนู',
  'business_type.created': 'เพิ่มประเภทธุรกิจ',
  'business_type.updated': 'แก้ประเภทธุรกิจ',
  'business_type.deleted': 'ลบประเภทธุรกิจ',
  'payment_method.created': 'เพิ่มช่องทางชำระเงิน',
  'payment_method.updated': 'แก้ช่องทางชำระเงิน',
  'payment_method.deleted': 'ลบช่องทางชำระเงิน',
  'system.settings_updated': 'แก้ตั้งค่าระบบ',
  'system.security_updated': 'แก้นโยบาย 2FA',
  'system.smtp_updated': 'แก้ตั้งค่าอีเมล (SMTP)',
  'support.ticket_updated': 'แก้เรื่องแจ้งปัญหา',
  'notification.template_updated': 'แก้เทมเพลตแจ้งเตือน',
}
const actionLabel = (a: string): string | null => (lang === 'th' ? ACTION_TH[a] ?? null : null)

const meta = (l: AuditLog, k: string): unknown => (l.metadata as Record<string, unknown> | null)?.[k]
const fmt = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

/** before/after compared field by field; arrays (e.g. permission lists) as added / removed items. */
function ChangeTable({ log }: { log: AuditLog }) {
  if (!log.beforeData && !log.afterData) return <p className="text-sm text-gray-500">{tx('No before/after data for this action.')}</p>
  const before = (log.beforeData ?? {}) as Record<string, unknown>
  const after = (log.afterData ?? {}) as Record<string, unknown>
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
  const rows = keys.filter((k) => JSON.stringify(before[k] ?? null) !== JSON.stringify(after[k] ?? null))
  if (!rows.length) return <p className="text-sm text-gray-500">{tx('No field changed.')}</p>
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="audit-changes">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 w-40">{tx('Field')}</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">{tx('Before')}</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">{tx('After')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((k) => {
            const b = before[k]
            const a = after[k]
            if (Array.isArray(b) || Array.isArray(a)) {
              const bs = (Array.isArray(b) ? b : []).map(String)
              const as = (Array.isArray(a) ? a : []).map(String)
              const added = as.filter((x) => !bs.includes(x))
              const removed = bs.filter((x) => !as.includes(x))
              return (
                <tr key={k} className="border-t border-gray-100 align-top">
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{k}</td>
                  <td className="px-3 py-2" colSpan={2}>
                    <div className="flex flex-wrap gap-1">
                      {added.map((x) => (
                        <span key={'+' + x} className="text-xs font-mono px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200">+ {x}</span>
                      ))}
                      {removed.map((x) => (
                        <span key={'-' + x} className="text-xs font-mono px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 line-through">{x}</span>
                      ))}
                      {!added.length && !removed.length && <span className="text-xs text-gray-400">{tx('Same items, different order')}</span>}
                    </div>
                  </td>
                </tr>
              )
            }
            return (
              <tr key={k} className="border-t border-gray-100 align-top">
                <td className="px-3 py-2 font-mono text-xs text-gray-600">{k}</td>
                <td className="px-3 py-2 text-red-700 break-all">{fmt(b)}</td>
                <td className="px-3 py-2 text-green-700 break-all">{fmt(a)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const AuditLogsPage: React.FC = () => {
  const [params] = useSearchParams()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [actionFilter, setActionFilter] = useState(params.get('action') ?? '')
  const [actorFilter, setActorFilter] = useState(params.get('actor') ?? '')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)

  const { data: actors = [] } = useQuery({
    queryKey: ['audit-actors'],
    queryFn: () => auditLogsApi.actors().then((r) => r.data.data ?? []),
    staleTime: 60_000,
  })
  const actorOptions = actors.filter((a) => a.id).map((a) => ({ label: `${a.name ?? a.id} (${a.count})`, value: a.id as string }))

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['audit-logs', { page, search, actionFilter, actorFilter, startDate, endDate }],
    queryFn: () =>
      auditLogsApi
        .list({
          page,
          limit: 20,
          search: search || undefined,
          action: actionFilter || undefined,
          actorAdminId: actorFilter || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        })
        .then((r) => r.data),
  })

  const logs = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1
  // any filter change goes back to page 1
  const withPage = (fn: (v: string) => void) => (v: string) => {
    fn(v)
    setPage(1)
  }

  const columns: Column<AuditLog>[] = [
    {
      key: 'createdAt', header: tx('Date'), sortable: true,
      render: (r) => <span className="text-gray-500 text-sm whitespace-nowrap">{formatDatetime(r.createdAt)}</span>,
    },
    {
      key: 'actorName', header: tx('Admin'),
      render: (r) => (
        <div>
          <p className="font-medium text-gray-800">{r.actorName ?? 'System'}</p>
          {typeof meta(r, 'actorEmail') === 'string' && <p className="text-xs text-gray-400">{meta(r, 'actorEmail') as string}</p>}
        </div>
      ),
    },
    {
      key: 'action', header: tx('Action'),
      render: (r) => (
        <div>
          {actionLabel(r.action) && (
            <p className={`text-sm ${r.action === 'permission.denied' || r.action === 'admin.login_failed' ? 'text-red-600' : 'text-gray-800'}`}>{actionLabel(r.action)}</p>
          )}
          <span className="font-mono text-xs text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">{r.action}</span>
        </div>
      ),
    },
    {
      key: 'targetType', header: tx('Target'),
      render: (r) => r.targetType ? (
        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono break-all">{r.targetType}{r.targetId ? `:${r.targetId}` : ''}</span>
      ) : <span className="text-gray-300">—</span>,
    },
    {
      key: 'shopId', header: tx('Shop'),
      render: (r) => <span className="text-gray-500 text-sm">{r.shopId ?? '—'}</span>,
    },
    {
      key: 'metadata', header: tx('Changed'),
      render: (r) => {
        const f = meta(r, 'changedFields')
        return Array.isArray(f) && f.length ? (
          <span className="text-xs text-gray-600">{(f as string[]).slice(0, 4).join(', ')}{f.length > 4 ? ` +${f.length - 4}` : ''}</span>
        ) : <span className="text-gray-300">—</span>
      },
    },
    {
      key: 'ipAddress', header: 'IP',
      render: (r) => <span className="text-xs font-mono text-gray-500">{r.ipAddress ?? '—'}</span>,
    },
  ]

  if (error) return <ErrorState message={tx('Failed to load audit logs.')} onRetry={() => refetch()} />

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{tx('Audit Logs')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{tx('Complete trail of all admin actions in the system')}</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <FilterBar
          search={{ value: search, onChange: withPage(setSearch), placeholder: tx('Search by admin name...') }}
          filters={[
            { key: 'action', label: tx('Action'), options: actionOptions, value: actionFilter, onChange: withPage(setActionFilter) },
            { key: 'actor', label: tx('Admin'), options: actorOptions, value: actorFilter, onChange: withPage(setActorFilter) },
          ]}
          onReset={() => { setSearch(''); setActionFilter(''); setActorFilter(''); setStartDate(''); setEndDate(''); setPage(1) }}
        />
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1) }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1) }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div>
        <DataTable
          columns={columns}
          data={logs}
          isLoading={isLoading}
          emptyMessage={tx('No audit logs found.')}
          onRowClick={(row) => setSelectedLog(row)}
        />
        <Pagination page={page} totalPages={totalPages} total={total} limit={20} onPageChange={setPage} />
      </div>

      <DetailDrawer
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title={tx('Audit Log Detail')}
        subtitle={selectedLog?.action}
        width="lg"
      >
        {selectedLog && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: tx('Action'), value: <span className="font-mono text-xs text-indigo-700 bg-indigo-50 px-2 py-1 rounded">{selectedLog.action}</span> },
                { label: tx('Admin'), value: selectedLog.actorName ?? 'System' },
                { label: tx('Target Type'), value: selectedLog.targetType ?? '—' },
                { label: tx('Target ID'), value: <span className="font-mono text-xs">{selectedLog.targetId ?? '—'}</span> },
                { label: tx('Shop ID'), value: <span className="font-mono text-xs">{selectedLog.shopId ?? '—'}</span> },
                { label: tx('Date'), value: formatDatetime(selectedLog.createdAt) },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">{label}</p>
                  <div className="mt-1 text-sm font-medium text-gray-800">{value}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm" data-testid="audit-who">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-400">{tx('Email / role')}</p>
                <p className="mt-1 text-gray-800 break-all">{fmt(meta(selectedLog, 'actorEmail'))} · {fmt(meta(selectedLog, 'actorRole'))}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-400">IP</p>
                <p className="mt-1 font-mono text-xs text-gray-800">{selectedLog.ipAddress ?? '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                <p className="text-xs text-gray-400">{tx('Request / device')}</p>
                <p className="mt-1 font-mono text-xs text-gray-700 break-all">
                  {fmt(meta(selectedLog, 'method'))} {fmt(meta(selectedLog, 'path'))}
                </p>
                <p className="mt-0.5 text-xs text-gray-500 break-all">{fmt(meta(selectedLog, 'userAgent'))}</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800 mb-2">{tx('What changed')}</p>
              <ChangeTable log={selectedLog} />
            </div>
            {selectedLog.beforeData && <JsonViewer data={selectedLog.beforeData} label={tx('Before State')} />}
            {selectedLog.afterData && <JsonViewer data={selectedLog.afterData} label={tx('After State')} />}
            {selectedLog.metadata && <JsonViewer data={selectedLog.metadata} label={tx('Metadata')} />}
          </div>
        )}
      </DetailDrawer>
    </div>
  )
}

export default AuditLogsPage
